import cuid from "cuid";
import { FieldArray, Formik } from "formik";
import { get, isEmpty, keyBy } from "lodash";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ATTACHMENT_TYPES,
  CONTACT_POINT_SYSTEM,
  GENDER,
  ORGANIZATION_TYPES,
  USER_TYPES,
} from "utils/constants";
import { bindArrayInputProps, bindInputProps } from "utils/helpers/input";
import IframeGenerator from "./iframe";
import validation from "./validation";
import ApiCredentialList from "./Widgets/ApiCredentialList";
import UsersTable from "./Widgets/UserList";
import Switch from "shared/components/Input/Switch";
import { useState } from "react";
import { useQuery } from "@apollo/client";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { MdAdd, MdDelete } from "react-icons/md";
import { COLORS } from "shared/style/colors";
import classNames from "classnames";
import Radio from "shared/components/Form/Radio";

const Detail = ({
  onSubmit,
  data,
  distributions,
  cancel,
  onDelete,
  acceptSignUp,
  updateParams,
  isCurrentBroker,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BROKER);
  const [insurancePlans, setInsurancePlans] = useState([]);
  useQuery(INSURANCE_PLANS, {
    variables: { where: { internal: false } },
    onCompleted: ({ insurancePlans: { data } }) => {
      setInsurancePlans(
        data.map(({ id, name, commissions }) => ({
          value: id,
          label: name,
          commissions,
        })),
      );
    },
  });

  const DETAIL = t("DETAIL", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const API_CREDENTIALS = t("API_CREDENTIALS", { returnObjects: true });
  const COMPANY_FORM_OPTIONS = t("COMPANY_FORM_OPTIONS", {
    returnObjects: true,
  });
  const telecoms = keyBy(get(data, "organization.telecoms"), "system");

  const handleFileUpload = async (file, setFieldValue) => {
    const { base64, size, uid, ...fileData } = file;
    setFieldValue("iconAttachment", {
      ...fileData,
      id: cuid(),
      content: base64,
      type: ATTACHMENT_TYPES.ICON,
    });
    setFieldValue("organization.icon", file.base64);
  };

  return (
    <Formik
      initialValues={{
        ...data,
        additionalInfo: {
          subGroupCode: "",
          populationCode: "",
          contributorType: "",
          ...get(data, "additionalInfo", {}),
        },
        telecoms,
        globalProjectsViewEnabled:
          get(data, "organization.config.globalProjectsViewEnabled") === true,
        hotListEnabled:
          get(data, "organization.config.hotList.enabled") === true,
        hotListSignalFeed:
          get(data, "organization.config.hotList.signalFeed") === true,
        hotListSourceName:
          get(data, "organization.config.hotList.sourceName") || "",
        leadTypologyEnabled:
          get(data, "organization.config.leadTypologyEnabled") === true,
        distributions: distributions?.map(({ commissions, ...rest }) => ({
          ...rest,
          commissions: commissions?.map(({ id }) => id),
        })),
      }}
      onSubmit={onSubmit}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        { isCurrentBroker },
      )}
    >
      {({ isValid, handleSubmit, setFieldValue, ...formProps }) => [
        <Header
          key={"header"}
          title={DETAIL.HEADER.TITLE}
          actions={[
            {
              text: DETAIL.HEADER.DISCARD,
              onClick: cancel,
              type: "primary--outlined",
              icon: <BiChevronLeft size={20} />,
            },
            // {
            //   text: DETAIL.HEADER.DELETE,
            //   onClick: onDelete,
            //   type: "danger--outlined",
            //   icon: <BiTrash size={20} />,
            // },
            {
              text: DETAIL.HEADER.SAVE,
              onClick: handleSubmit,
              type: "primary",
              icon: <BiCheck size={20} />,
            },
          ]}
        />,
        <div key="organization">
          <Card type={"simple"} head title={FORM.ADMIN_INFO}>
            <div className="grid--2">
              <Input
                placeholder={FORM.BROKER_CODE}
                label={FORM.BROKER_CODE}
                {...bindInputProps({ name: "code", ...formProps })}
              />
              <Input
                type="checkbox"
                label={FORM.INTERNAL}
                disabled
                {...bindInputProps({ name: "isInternal", ...formProps })}
              />
              <Input
                placeholder={FORM.SOCIAL_REASON}
                label={FORM.SOCIAL_REASON}
                {...bindInputProps({ name: "socialReason", ...formProps })}
                required
              />
              <Input
                placeholder={FORM.BUSINESS_NAME}
                label={FORM.BUSINESS_NAME}
                {...bindInputProps({ name: "organization.name", ...formProps })}
                required
              />
              <Select
                label={FORM.CATEGORY.LABEL}
                options={Object.values(ORGANIZATION_TYPES).map((value) => ({
                  value,
                  label: FORM.CATEGORY.OPTIONS[value],
                }))}
                {...bindInputProps({ name: "organization.type", ...formProps })}
                disabled
              />
              <Input
                placeholder={FORM.ORIAS_CODE}
                label={FORM.ORIAS_CODE}
                disabled
                {...bindInputProps({ name: "orias", ...formProps })}
              />
              <Input
                placeholder={FORM.SUB_GROUP_CODE}
                label={FORM.SUB_GROUP_CODE}
                {...bindInputProps({
                  name: "additionalInfo.subGroupCode",
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.POPULATION_CODE}
                label={FORM.POPULATION_CODE}
                {...bindInputProps({
                  name: "additionalInfo.populationCode",
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.CONTRIBUTOR_TYPE}
                label={FORM.CONTRIBUTOR_TYPE}
                {...bindInputProps({
                  name: "additionalInfo.contributorType",
                  ...formProps,
                })}
              />
              {/* <Select
                label={FORM.COMPANY_FORM}
                placeholder={FORM.COMPANY_FORM}
                options={Object.values(ORGANIZATION_FORMS).map((value) => ({
                  value,
                  label: COMPANY_FORM_OPTIONS[value],
                }))}
                {...bindInputProps({ name: "organization.form", ...formProps })}
                required
              /> */}
              <Input
                placeholder={FORM.ORGANIZATION_PHONE}
                label={FORM.ORGANIZATION_PHONE}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.ORGANIZATION_EMAIL}
                label={FORM.ORGANIZATION_EMAIL}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.POSTAL_ADDRESS}
                label={FORM.POSTAL_ADDRESS}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.ZIP_CODE}
                label={FORM.ZIP_CODE}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.CITY}
                label={FORM.CITY}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.RCS_NUMBER}
                label={FORM.RCS_NUMBER}
                disabled
                {...bindInputProps({ name: "rcsNumber", ...formProps })}
              />
              <Input
                type="textarea"
                placeholder={FORM.RCS_PLACE}
                label={FORM.RCS_PLACE}
                disabled
                {...bindInputProps({ name: "rcsPlace", ...formProps })}
              />
              <Input
                placeholder={FORM.SIREN_NUMBER}
                label={FORM.SIREN_NUMBER}
                {...bindInputProps({ name: "sirenNumber", ...formProps })}
              />
              <Input
                type="file"
                variant="uploader"
                listType="picture"
                showUploadList={false}
                maxCount={1}
                onUpload={([file]) => handleFileUpload(file, setFieldValue)}
                fileList={
                  isEmpty(get(formProps, "values.organization.icon", []))
                    ? []
                    : [get(formProps, "values.organization.icon")]
                }
              >
                <Button type="primary--link">{FORM.ICON}</Button>
                <div>
                  {get(formProps, "values.organization.icon") && (
                    <img
                      className="icon"
                      alt="icon"
                      src={get(formProps, "values.organization.icon")}
                    />
                  )}
                </div>
              </Input>
              <Select
                label={FORM.GLOBAL_PROJECTS_VIEW}
                placeholder={FORM.GLOBAL_PROJECTS_VIEW}
                options={[
                  { label: FORM.GLOBAL_PROJECTS_VIEW_ON, value: true },
                  { label: FORM.GLOBAL_PROJECTS_VIEW_OFF, value: false },
                ]}
                value={get(formProps, "values.globalProjectsViewEnabled")}
                onChange={(value) =>
                  setFieldValue("globalProjectsViewEnabled", value)
                }
              />
              <Select
                label={FORM.HOT_LIST}
                placeholder={FORM.HOT_LIST}
                options={[
                  { label: FORM.HOT_LIST_ON, value: true },
                  { label: FORM.HOT_LIST_OFF, value: false },
                ]}
                value={get(formProps, "values.hotListEnabled")}
                onChange={(value) => setFieldValue("hotListEnabled", value)}
              />
              {get(formProps, "values.hotListEnabled") && (
                <Input
                  placeholder={FORM.HOT_LIST_SOURCE_PLACEHOLDER}
                  label={FORM.HOT_LIST_SOURCE}
                  {...bindInputProps({
                    name: "hotListSourceName",
                    ...formProps,
                  })}
                />
              )}
              {get(formProps, "values.hotListEnabled") && (
                <Select
                  label={FORM.HOT_LIST_SIGNAL_FEED}
                  placeholder={FORM.HOT_LIST_SIGNAL_FEED}
                  options={[
                    { label: FORM.HOT_LIST_ON, value: true },
                    { label: FORM.HOT_LIST_OFF, value: false },
                  ]}
                  value={get(formProps, "values.hotListSignalFeed")}
                  onChange={(value) =>
                    setFieldValue("hotListSignalFeed", value)
                  }
                />
              )}
              <Select
                label={FORM.LEAD_TYPOLOGY_VIEW}
                placeholder={FORM.LEAD_TYPOLOGY_VIEW}
                options={[
                  { label: FORM.LEAD_TYPOLOGY_VIEW_ON, value: true },
                  { label: FORM.LEAD_TYPOLOGY_VIEW_OFF, value: false },
                ]}
                value={get(formProps, "values.leadTypologyEnabled")}
                onChange={(value) =>
                  setFieldValue("leadTypologyEnabled", value)
                }
              />
            </div>
          </Card>
        </div>,
        <div key="divider-div" className="m-bottom-24" />,
        <div
          key="capital"
          className={classNames({
            hidden:
              get(formProps, "values.user.meta.type") ===
              USER_TYPES.INTERNAL_SUPERVISOR.type,
          })}
        >
          <Card type={"simple"} head title={FORM.CAPITAL_DEPENDENCE}>
            <div className="grid--1">
              <div className="grid--1 m-bottom-24">
                <h4>{FORM.REMUNERATION}</h4>
                <div className="d-flex justify--between">
                  <span>{FORM.BASED_ON_FEES}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.OPTIONS.YES}
                    unCheckedChildren={FORM.OPTIONS.NO}
                    defaultChecked={get(
                      formProps.values,
                      "additionalInfo.basedOnFees",
                    )}
                    onChange={(e, _) =>
                      setFieldValue("additionalInfo.basedOnFees", e)
                    }
                  />
                </div>
                <div className="d-flex justify--between">
                  <span>{FORM.BASE_ON_COMMISSION}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.OPTIONS.YES}
                    unCheckedChildren={FORM.OPTIONS.NO}
                    defaultChecked={get(
                      formProps.values,
                      "additionalInfo.basedOnCommission",
                    )}
                    onChange={(e, _) =>
                      setFieldValue("additionalInfo.basedOnCommission", e)
                    }
                  />
                </div>
              </div>
              <div className="grid--1 m-bottom-24">
                <h4>{FORM.FINANCIAL_LINKS}</h4>
                <div className="d-flex justify--between">
                  <span>{FORM.CAPITAL_PLUS_TEN}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.OPTIONS.YES}
                    unCheckedChildren={FORM.OPTIONS.NO}
                    defaultChecked={get(
                      formProps.values,
                      "additionalInfo.capitalPlusTen",
                    )}
                    onChange={(e, _) =>
                      setFieldValue("additionalInfo.capitalPlusTen", e)
                    }
                  />
                </div>
                <div className="d-flex justify--between">
                  <span>{FORM.FIRM_PLUS_TEN}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.OPTIONS.YES}
                    unCheckedChildren={FORM.OPTIONS.NO}
                    defaultChecked={get(
                      formProps.values,
                      "additionalInfo.firmPlusTen",
                    )}
                    onChange={(e, _) =>
                      setFieldValue("additionalInfo.firmPlusTen", e)
                    }
                  />
                </div>
                <div className="d-flex justify--between">
                  <span>{FORM.LAST_EXERCISE}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.OPTIONS.YES}
                    unCheckedChildren={FORM.OPTIONS.NO}
                    defaultChecked={get(
                      formProps.values,
                      "additionalInfo.lastExercise",
                    )}
                    onChange={(e, _) =>
                      setFieldValue("additionalInfo.lastExercise", e)
                    }
                  />
                </div>
              </div>
              <div className="grid--1">
                <h4>{FORM.EXCLUSIVE_LINKS}</h4>
                <div className="d-flex justify--between">
                  <span>{FORM.CONTRACTUAL_OBLIGATION}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.OPTIONS.YES}
                    unCheckedChildren={FORM.OPTIONS.NO}
                    defaultChecked={get(
                      formProps.values,
                      "additionalInfo.contractualObligation",
                    )}
                    onChange={(e, _) =>
                      setFieldValue("additionalInfo.contractualObligation", e)
                    }
                  />
                </div>
                <div className="d-flex justify--between">
                  <span>{FORM.ANALYSIS_MARKET_CONTRACTS}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.OPTIONS.YES}
                    unCheckedChildren={FORM.OPTIONS.NO}
                    defaultChecked={get(
                      formProps.values,
                      "additionalInfo.analysisMarketContracts",
                    )}
                    onChange={(e, _) =>
                      setFieldValue("additionalInfo.analysisMarketContracts", e)
                    }
                  />
                </div>
                <div className="d-flex justify--between">
                  <span>{FORM.RECOMMENDATION_HEALTH_INSURANCE}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.OPTIONS.YES}
                    unCheckedChildren={FORM.OPTIONS.NO}
                    defaultChecked={get(
                      formProps.values,
                      "additionalInfo.recommendationHealthInsurance",
                    )}
                    onChange={(e, _) =>
                      setFieldValue(
                        "additionalInfo.recommendationHealthInsurance",
                        e,
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>,
        <div key="org-div" className="m-bottom-24" />,
        <div
          key="user"
          className={classNames({
            hidden:
              get(formProps, "values.user.meta.type") ===
              USER_TYPES.INTERNAL_SUPERVISOR.type,
          })}
        >
          <Card type={"simple"} head title={FORM.LEGAL_INFO}>
            <div className="grid--2">
              <Radio
                isHorizontal={false}
                label={FORM.CIVILITY.LABEL}
                options={[
                  {
                    label: FORM.CIVILITY.MALE,
                    value: GENDER.MALE,
                  },
                  {
                    label: FORM.CIVILITY.FEMALE,
                    value: GENDER.FEMALE,
                  },
                ]}
                disabled
                {...bindInputProps({ name: "user.gender", ...formProps })}
              />
              <Input
                placeholder={FORM.FIRST_NAME}
                label={FORM.FIRST_NAME}
                disabled
                {...bindInputProps({ name: "user.firstname", ...formProps })}
              />
              <Input
                placeholder={FORM.LAST_NAME}
                label={FORM.LAST_NAME}
                disabled
                {...bindInputProps({ name: "user.lastname", ...formProps })}
              />
              <Input
                placeholder={FORM.PHONE}
                label={FORM.PHONE}
                disabled
                {...bindInputProps({ name: "user.phone", ...formProps })}
              />
              <Input
                placeholder={FORM.EMAIL}
                label={FORM.EMAIL}
                disabled
                {...bindInputProps({ name: "user.email", ...formProps })}
              />
            </div>
          </Card>
          <div key="user-div" className="m-bottom-24" />
        </div>,
        !isCurrentBroker && (
          <div
            key="distribution"
            className={classNames({
              hidden:
                get(formProps, "values.user.meta.type") ===
                USER_TYPES.INTERNAL_SUPERVISOR.type,
            })}
          >
            <Card title={FORM.DISTRIBUTION}>
              <FieldArray
                name={"distributions"}
                render={(arrayHelpers) => [
                  get(formProps, "values.distributions", []).map((_, i) => (
                    <div className="d-flex flex--wrap" key={_.id}>
                      <div className="grid--3 flex--grow-1 m-bottom-16">
                        <Select
                          options={insurancePlans.filter(
                            (plan) =>
                              !get(formProps, "values.distributions", []).some(
                                (distribution, index) =>
                                  index !== i &&
                                  distribution.insurancePlan &&
                                  distribution.insurancePlan.id === plan.value,
                              ),
                          )}
                          placeholder={FORM.INSURANCE_PLAN}
                          label={FORM.INSURANCE_PLAN}
                          {...bindArrayInputProps({
                            parent: "distributions",
                            index: i,
                            name: "insurancePlan.id",
                            ...formProps,
                          })}
                          onChange={(value) => {
                            setFieldValue(`distributions.${i}.commissions`, []);
                            setFieldValue(
                              `distributions.${i}.insurancePlan.id`,
                              value,
                            );
                          }}
                          required
                        />
                        <Select
                          mode="multiple"
                          options={get(
                            insurancePlans.find(
                              ({ value }) =>
                                value ===
                                get(
                                  formProps,
                                  `values.distributions.${i}.insurancePlan.id`,
                                ),
                            ),
                            "commissions",
                            [],
                          ).map(({ id, value, withholding }) => ({
                            value: id,
                            label: `${value}/${withholding || value}`,
                          }))}
                          placeholder={FORM.COMMISSIONS}
                          label={FORM.COMMISSIONS}
                          {...bindArrayInputProps({
                            parent: "distributions",
                            index: i,
                            name: "commissions",
                            ...formProps,
                          })}
                          onChange={(values) =>
                            setFieldValue(
                              `distributions.${i}.commissions`,
                              values,
                            )
                          }
                          required
                        />
                        <MdDelete
                          size={24}
                          color={COLORS.C_DANGER}
                          onClick={() => arrayHelpers.remove(i)}
                        />
                      </div>
                    </div>
                  )),
                  <Button
                    type="primary"
                    key="add-row"
                    onClick={() => {
                      arrayHelpers.push({
                        id: cuid(),
                        insurancePlan: { id: null },
                        commissions: [],
                      });
                    }}
                  >
                    <MdAdd />
                  </Button>,
                ]}
              />
            </Card>
            <div key="distribution-div" className="m-bottom-24" />
          </div>
        ),
        <Card key="list" type={"simple"}>
          <UsersTable
            data={data}
            acceptSignUp={acceptSignUp}
            updateParams={updateParams}
          />
        </Card>,
        <div key="iframe-div" className="m-bottom-24" />,
        <div key="api-credentials" className="m-bottom-24">
          <Card title={API_CREDENTIALS.TITLE}>
            <ApiCredentialList organizationId={get(data, "organization.id")} />
          </Card>
        </div>,
        <Card title={FORM.IFRAME_GENERATOR.TITLE} key="iframe">
          <IframeGenerator
            organizationId={get(data, "organization.id")}
            translation={FORM.IFRAME_GENERATOR}
          />
        </Card>,
      ]}
    </Formik>
  );
};

export default Detail;
